import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Visibility } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { HumanMeanResponse } from '@/api/bol';
import {
  formatDateTime,
  formatExternalReferences,
  formatSpecialCharacteristics,
} from '@/utils/datagridFormatters';

interface HumanMeanColumnsProps {
  onDelete: (humanMean: HumanMeanResponse) => void;
  canDelete?: boolean;
  deleteTooltip?: string;
}

export const createHumanMeanColumns = ({ onDelete, canDelete = true, deleteTooltip = '' }: HumanMeanColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1.6,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { id?: string }) => row.id || '-',
  },
  {
    field: 'role',
    headerName: 'Role',
    flex: 1,
    minWidth: 140,
    valueGetter: (_value: unknown, row: { role?: string }) => row.role || '-',
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 1.3,
    minWidth: 200,
    valueGetter: (_value: unknown, row: { description?: string }) => row.description || '-',
  },
  {
    field: 'allocationDate',
    headerName: 'Allocation Date',
    flex: 1,
    minWidth: 160,
    valueGetter: (_value: unknown, row: { allocationDate?: string }) => formatDateTime(row.allocationDate),
  },
  {
    field: 'piecesCount',
    headerName: 'Pieces',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (_value: unknown, row: { pieces?: unknown[] }) => row.pieces?.length || 0,
  },
  {
    field: 'linkedResourcesCount',
    headerName: 'Linked Resources',
    flex: 0.9,
    minWidth: 140,
    valueGetter: (_value: unknown, row: { isLinkedToResources?: unknown[] }) => row.isLinkedToResources?.length || 0,
  },
  {
    field: 'contactsCount',
    headerName: 'Contacts',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (_value: unknown, row: { contacts?: unknown[] }) => row.contacts?.length || 0,
  },
  {
    field: 'externalReferences',
    headerName: 'External References',
    flex: 1.4,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { externalReferences?: Array<{ externalReferenceType?: string | null; reference?: string | null }> }) =>
      formatExternalReferences(row.externalReferences),
  },
  {
    field: 'specialCharacteristics',
    headerName: 'Special Characteristics',
    flex: 1.4,
    minWidth: 220,
    valueGetter: (
      _value: unknown,
      row: {
        specialCharacteristics?: Array<{
          specialCharacteristicType?: string | null;
          characteristicReference?: string | null;
          stringValue?: string | null;
          numericalValue?: { value?: number; unit?: string } | null;
        }>;
      }
    ) => formatSpecialCharacteristics(row.specialCharacteristics),
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 120,
    getActions: (params) => [
      <Tooltip key="view" title="View details">
        <IconButton
          component={Link}
          to={`/human-means/${params.row.id as string}`}
          size="small"
          color="primary"
          aria-label="View Details"
        >
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>,
      <GridActionsCellItem
        key="delete"
        icon={
          <Tooltip title={!canDelete ? deleteTooltip : 'Delete'}>
            <span>
              <Delete color={!canDelete ? 'disabled' : 'error'} />
            </span>
          </Tooltip>
        }
        label="Delete"
        onClick={() => onDelete(params.row as HumanMeanResponse)}
        disabled={!canDelete}
        showInMenu={false}
      />,
    ],
  },
];

export const defaultHumanMeanColumnVisibilityModel = {
  id: true,
  role: true,
  description: true,
  allocationDate: true,
  piecesCount: true,
  linkedResourcesCount: false,
  contactsCount: false,
  externalReferences: false,
  specialCharacteristics: false,
};
